import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  MinusOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { RiStackLine } from '@remixicon/react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FeatureGroup,
  Marker,
  useMap,
  useMapEvent,
  useMapEvents,
  Tooltip,
} from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { toNM } from '../../report/components/helperFunction';
import L from 'leaflet';
import React from 'react';

interface Props {
  showMapPolyline?: boolean;
  showMapSettings?: boolean;
  showMapLayers?: boolean;
  handleOpenModalSettings?: () => void;
  handleOpenModalLayers?: () => void;
}

function computeDistances(points: any) {
  let segmentDistances = [];
  let cumulative = [];
  let total = 0;

  for (let i = 0; i < points.length; i++) {
    if (i === 0) {
      segmentDistances.push(0);
      cumulative.push(0);
    } else {
      const prev = L.latLng(points[i - 1]);
      const curr = L.latLng(points[i]);
      const d = prev.distanceTo(curr); // meters
      total += d;
      segmentDistances.push(d);
      cumulative.push(total);
    }
  }
  return { segmentDistances, cumulative, total };
}

/**
 * The options of the draw control keep one identity: leaflet-draw writes its
 * defaults into them, so options made on each render no longer equal the
 * first ones and EditControl rebuilds the control, cancelling the polyline
 * being drawn whenever the map re-renders, e.g. on a poll of the last data.
 */
const LINE_COLOR = 'rgb(217 171 13)';
// a selected line and its points, in view and in edit
const SELECTED_LINE_COLOR = 'rgb(0 209 255)';

const DRAW_OPTIONS = {
  polyline: {
    shapeOptions: {
      color: LINE_COLOR,
      opacity: 0.7,
      lineJoin: 'round',
      weight: 4,
    },
    showLength: true,
    guidelineDistance: 10,
  },
  polygon: false,
  rectangle: false,
  circle: false,
  marker: false,
  circlemarker: false,
} as const;

const EDIT_OPTIONS = { edit: false, remove: true } as const;

const LINE_STYLE: L.PathOptions = {
  color: LINE_COLOR,
  opacity: 0.7,
  weight: 4,
};
const SELECTED_LINE_STYLE: L.PathOptions = {
  color: SELECTED_LINE_COLOR,
  opacity: 1,
  weight: 5,
};

function createCircleDivIcon(color: string) {
  return L.divIcon({
    html: `
      <div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;">
        <div style="width: 15px; height: 15px; border-radius: 100px; background: white; border: 3px solid ${color};"></div>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

type DrawnLine = {
  id: number;
  points: [number, number][];
};

// how far the faded point to extend a line sits after its end, in pixels
const EXTEND_OFFSET = 40;

function getLineLatLngs(layer: L.Polyline): L.LatLng[] {
  const latlngs: any = layer.getLatLngs();
  return Array.isArray(latlngs[0]) ? latlngs.flat() : latlngs;
}

// the faded point after the end, along the last segment of the line
function getExtendLatLng(map: L.Map, layer: L.Polyline): L.LatLng {
  const latlngs = getLineLatLngs(layer);
  const end = map.latLngToLayerPoint(latlngs[latlngs.length - 1]);
  const before =
    latlngs.length > 1
      ? map.latLngToLayerPoint(latlngs[latlngs.length - 2])
      : end.subtract(L.point(1, 0));
  const direction = end.subtract(before);
  const length = Math.hypot(direction.x, direction.y) || 1;
  return map.layerPointToLatLng(
    end.add(direction.multiplyBy(EXTEND_OFFSET / length)),
  );
}

/**
 * Inserts a point clicked on a line between the two points of the segment
 * it is on. The editor of the line keeps the array of its points, so the
 * point goes into that array.
 */
function insertLinePoint(map: L.Map, layer: any, latlng: L.LatLng) {
  const latlngs = getLineLatLngs(layer);
  const point = map.latLngToLayerPoint(latlng);
  let segment = 0;
  let closest = Infinity;
  for (let i = 0; i < latlngs.length - 1; i++) {
    const distance = L.LineUtil.pointToSegmentDistance(
      point,
      map.latLngToLayerPoint(latlngs[i]),
      map.latLngToLayerPoint(latlngs[i + 1]),
    );
    if (distance < closest) {
      closest = distance;
      segment = i;
    }
  }
  latlngs.splice(segment + 1, 0, latlng);
  layer.redraw();
  layer.editing.updateMarkers();
  layer.fire('edit');
}

function getLinePoints(layer: L.Polyline): [number, number][] {
  let latlngs: any = layer.getLatLngs();
  if (Array.isArray(latlngs[0])) {
    latlngs = latlngs.flat();
  }
  return latlngs.map((p: L.LatLng) => [p.lat, p.lng]);
}

const MapControl: React.FC<Props> = (props) => {
  const map = useMap();
  const path = useLocation();

  const increaseMapZoom = () => {
    const currZoom = map.getZoom();
    map.setZoom(currZoom + 1);
  };

  const decreaseMapZoom = () => {
    const currZoom = map.getZoom();
    map.setZoom(currZoom - 1);
  };

  const [lines, setLines] = useState<DrawnLine[]>([]);
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [editingLineId, setEditingLineId] = useState<number | null>(null);
  const featureGroupRef = useRef<L.FeatureGroup>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  /**
   * EditControl calls the handlers of its first render, so they only use
   * state setters and these refs.
   */
  const layersRef = useRef(new Map<number, L.Polyline>());
  const isDeletingRef = useRef(false);
  const editingLineIdRef = useRef<number | null>(null);
  editingLineIdRef.current = editingLineId;

  const selectLine = (id: number) => {
    if (!isDeletingRef.current) {
      setSelectedLineId(id);
    }
  };

  // ends the editing of every line and keeps the points it left
  const finishEditing = () => {
    layersRef.current.forEach((layer: any) => {
      if (layer.editing?.enabled()) {
        layer.editing.disable();
      }
    });
    setLines((prev) =>
      prev.map((line) => {
        const layer = layersRef.current.get(line.id);
        return layer ? { ...line, points: getLinePoints(layer) } : line;
      }),
    );
    setEditingLineId(null);
  };

  const handleOnCreatePolyline = (e: any) => {
    const layer = e.layer as L.Polyline;
    if (layer) {
      const id = L.stamp(layer);
      layersRef.current.set(id, layer);
      layer.on('click', (event: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(event);
        // a click on the line being edited adds a point there
        if (editingLineIdRef.current === id) {
          insertLinePoint(map, layer, event.latlng);
        } else {
          selectLine(id);
        }
      });

      setLines((prev) => [...prev, { id, points: getLinePoints(layer) }]);
    }
  };

  const handleOnDrawStart = () => {
    finishEditing();
    setSelectedLineId(null);
  };

  const handleOnDeleteStart = () => {
    isDeletingRef.current = true;
    handleOnDrawStart();
  };

  const handleOnDeleteStop = () => {
    isDeletingRef.current = false;
  };

  const formatDistance = (m: any) => {
    const km = m / 1000;
    const nm = m / 1852;
    return `${km.toFixed(2)} km | ${nm.toFixed(2)} NM`;
  };

  /**
   * The delete mode of the toolbar only clears every line, or is cancelled:
   * one line is deleted with the button of the selected line, so the mode
   * has no Save action and a click on a line does not remove it.
   */
  const handleOnMounted = (control: any) => {
    const toolbar = control._toolbars?.edit;
    const handler = toolbar?._modes?.remove?.handler;
    if (!toolbar || !handler) {
      return;
    }

    const getActions = toolbar.getActions;
    toolbar.getActions = function (this: any, mode: any) {
      return getActions
        .call(this, mode)
        .filter((action: any) => action.callback !== this._save);
    };

    handler._enableLayerDelete = () => {};

    const addHooks = handler.addHooks;
    handler.addHooks = function (this: any) {
      addHooks.call(this);
      // an empty tooltip is hidden: "Click on a feature to remove."
      this._tooltip?.updateContent({ text: '' });
    };
  };

  // only the lines removed in the delete mode of the toolbar
  function handleOnDeletePolyline(e: any) {
    const ids: number[] = [];
    e.layers.eachLayer((layer: L.Layer) => {
      ids.push(L.stamp(layer));
      layersRef.current.delete(L.stamp(layer));
    });
    setLines((prev) => prev.filter((line) => !ids.includes(line.id)));
  }

  const handleEditLine = () => {
    const layer: any =
      selectedLineId !== null && layersRef.current.get(selectedLineId);
    if (layer?.editing) {
      // the points of the editor are the circles of the line, the faded
      // ones between them add a point
      const icon = createCircleDivIcon(SELECTED_LINE_COLOR);
      layer.options.poly = { icon, touchIcon: icon };
      layer.editing.enable();
      setEditingLineId(selectedLineId);
    }
  };

  const handleDeleteLine = () => {
    if (selectedLineId === null) {
      return;
    }
    const layer: any = layersRef.current.get(selectedLineId);
    if (layer) {
      layer.editing?.disable();
      featureGroupRef.current?.removeLayer(layer);
      layersRef.current.delete(selectedLineId);
    }
    setLines((prev) => prev.filter((line) => line.id !== selectedLineId));
    setSelectedLineId(null);
    setEditingLineId(null);
  };

  // selecting another line ends the editing of the one before
  useEffect(() => {
    if (editingLineId !== null && editingLineId !== selectedLineId) {
      finishEditing();
    }
  }, [selectedLineId, editingLineId]);

  useEffect(() => {
    layersRef.current.forEach((layer, id) =>
      layer.setStyle(id === selectedLineId ? SELECTED_LINE_STYLE : LINE_STYLE),
    );
  }, [selectedLineId, lines]);

  // a click on the map leaves the selected line, unless it is being edited
  useMapEvent('click', () => {
    if (editingLineId === null) {
      setSelectedLineId(null);
    }
  });

  /**
   * A faded point after the end of the line being edited, linked to it by a
   * faded dashed line: dragged or clicked, it adds a point after the end.
   */
  useEffect(() => {
    const layer: any =
      editingLineId !== null && layersRef.current.get(editingLineId);
    if (!layer?.editing) {
      return;
    }

    const extendPoint = L.marker(getExtendLatLng(map, layer), {
      icon: createCircleDivIcon(SELECTED_LINE_COLOR),
      draggable: true,
      opacity: 0.6,
    }).addTo(map);
    const extendGuide = L.polyline([], {
      color: SELECTED_LINE_COLOR,
      opacity: 0.6,
      weight: 2,
      dashArray: '4 6',
      interactive: false,
    }).addTo(map);

    const showGuide = () => {
      const latlngs = getLineLatLngs(layer);
      extendGuide.setLatLngs([
        latlngs[latlngs.length - 1],
        extendPoint.getLatLng(),
      ]);
    };
    const place = () => {
      extendPoint.setLatLng(getExtendLatLng(map, layer));
      showGuide();
    };
    const extend = () => {
      layer.addLatLng(extendPoint.getLatLng());
      layer.editing.updateMarkers();
      layer.fire('edit');
    };

    extendPoint.on('click dragend', extend).on('drag', showGuide);
    layer.on('edit editdrag', place);
    map.on('zoomend', place);
    showGuide();

    return () => {
      extendPoint.remove();
      extendGuide.remove();
      layer.off('edit editdrag', place);
      map.off('zoomend', place);
    };
  }, [map, editingLineId]);

  // the buttons are on the map, their clicks are not clicks on the map
  useEffect(() => {
    if (containerRef.current) {
      L.DomEvent.disableClickPropagation(containerRef.current);
    }
  }, []);

  /**
   * The buttons of the selected line sit under the draw toolbar: a control
   * added after it in the top right corner, the buttons rendered into it.
   */
  const [lineControl, setLineControl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!props.showMapPolyline) {
      return;
    }
    const control = new L.Control({ position: 'topright' });
    control.onAdd = () => {
      const container = L.DomUtil.create('div');
      L.DomEvent.disableClickPropagation(container);
      return container;
    };
    control.addTo(map);
    setLineControl(control.getContainer() || null);

    return () => {
      control.remove();
      setLineControl(null);
    };
  }, [map, props.showMapPolyline]);

  useEffect(() => {
    if (lineControl) {
      lineControl.style.display = selectedLineId === null ? 'none' : '';
    }
  }, [lineControl, selectedLineId]);

  return (
    <MapControlContainer ref={containerRef} path={path.pathname}>
      {props.showMapPolyline && (
        <FeatureGroup ref={featureGroupRef} key='drawing-layer'>
          <EditControl
            draw={DRAW_OPTIONS}
            edit={EDIT_OPTIONS}
            onCreated={(e) => handleOnCreatePolyline(e)}
            onDeleted={handleOnDeletePolyline}
            onDrawStart={handleOnDrawStart}
            onDeleteStart={handleOnDeleteStart}
            onDeleteStop={handleOnDeleteStop}
            onMounted={handleOnMounted}
            position='topright'
          />
        </FeatureGroup>
      )}

      {/* the points of a line being edited are the markers of its editor */}
      {lines
        .filter((line) => line.id !== editingLineId)
        .map((line) => {
          const { cumulative } = computeDistances(line.points);
          return (
            <React.Fragment key={line.id}>
              {line.points.map((item, idx2: number) => (
                <Marker
                  key={`${line.id}-${idx2}`}
                  position={item}
                  icon={createCircleDivIcon(
                    line.id === selectedLineId
                      ? SELECTED_LINE_COLOR
                      : LINE_COLOR,
                  )}
                  eventHandlers={{ click: () => selectLine(line.id) }}
                >
                  <Tooltip direction='top' permanent={false}>
                    {idx2 === 0
                      ? 'Start'
                      : `Distance: ${formatDistance(cumulative[idx2])}`}
                  </Tooltip>
                </Marker>
              ))}
            </React.Fragment>
          );
        })}

      {/* Selected Polyline, under the draw toolbar */}
      {lineControl &&
        selectedLineId !== null &&
        createPortal(
          <LineButtons className='leaflet-bar'>
            {editingLineId === null ? (
              <LineButton title='Edit Polyline' onClick={handleEditLine}>
                <EditOutlined />
              </LineButton>
            ) : (
              <LineButton title='Done Editing' onClick={finishEditing}>
                <CheckOutlined />
              </LineButton>
            )}
            <LineButton title='Delete Polyline' onClick={handleDeleteLine}>
              <DeleteOutlined />
            </LineButton>
            {editingLineId !== null && (
              <EditHint>
                Drag a point to move it, click a point to delete it. Click the
                line or a faded point on it to add a point there, drag or click
                the faded point after the end to extend the line.
              </EditHint>
            )}
          </LineButtons>,
          lineControl,
        )}

      {/* Map Settings */}
      {props.showMapSettings && (
        <BtnIconGroup>
          <BtnIcon
            title='Map Settings'
            onClick={() =>
              props.handleOpenModalSettings && props.handleOpenModalSettings()
            }
          >
            <SettingOutlined style={{ fontSize: 14 }} />
          </BtnIcon>
        </BtnIconGroup>
      )}

      {/* Map Layers */}
      {props.showMapLayers && (
        <BtnIconGroup>
          <BtnIcon
            title='Map Layers'
            onClick={() =>
              props.handleOpenModalLayers && props.handleOpenModalLayers()
            }
          >
            <RiStackLine style={{ width: 16 }} />
          </BtnIcon>
        </BtnIconGroup>
      )}

      {/* Zoom Control */}
      <BtnIconGroup>
        <BtnIcon title='Zoom In Map' onClick={increaseMapZoom}>
          {/* <img src="/images/icon-add.svg" /> */}
          <PlusOutlined />
        </BtnIcon>
        <BtnIcon title='Zoom Out Map' onClick={decreaseMapZoom}>
          {/* <img src="/images/icon-remove.svg" /> */}
          <MinusOutlined />
        </BtnIcon>
      </BtnIconGroup>
    </MapControlContainer>
  );
};

const MapControlContainer = styled.div<{ path?: any }>`
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
  position: absolute;
  z-index: 991;
  bottom: ${(props) => (props.path === '/home' ? '110px' : '50px')};
  right: 12px;

  @media (max-width: 767px) {
    gap: 10px;
    right: 10px;
  }
`;

const BtnIconGroup = styled.div`
  border-radius: 2px;
  background-color: #e8ecf3;
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0px 2px rgb(0 0 0 / 20%);
`;

const BtnIcon = styled.div`
  height: 30px;
  width: 30px;
  border-radius: 4px;
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  img {
    width: 16px;
  }

  @media (max-width: 767px) {
    width: 32px;
    height: 32px;
  }
`;

const LineButtons = styled.div`
  position: relative;
  background-color: white;
`;

// the size of the buttons of the draw toolbar above
const LineButton = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  color: #464646;

  & + & {
    border-top: 1px solid #ccc;
  }

  &:hover {
    background-color: #f4f4f4;
  }

  /*
   * The icon changes with the state the click sets, so the click on the map
   * would come from an element no longer in the page, which Leaflet takes
   * for a click on the map. The button itself is the target.
   */
  & > * {
    pointer-events: none;
  }
`;

const EditHint = styled.div`
  position: absolute;
  top: 0;
  right: 40px;
  width: 220px;
  padding: 6px 10px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 12px;
  line-height: 1.4;
`;

export default MapControl;
